import { useSelector } from "@xstate/react";
import dynamic from "next/dynamic";
import { useEffect, useMemo } from "react";
import { P, match } from "ts-pattern";

import { useRouteParams } from "~/data/route";
import { useDocumentActor, usePageActor } from "~/state";

import { useLogger } from "../canvas/hooks/useLogger";
import { CanvasFailure } from "../canvas/views/CanvasFailure";
import { CanvasLoading } from "../canvas/views/CanvasLoading";
import { CanvasPage } from "../canvas/views/CanvasPage";
import { DocumentTopBar } from "../canvases/DocumentTopBar";

const PageComponent: React.FC = () => {
	const logger = useLogger("Page");

	const { workspaceName, documentId, pageSlug } = useRouteParams();

	const documentActor = useDocumentActor(documentId);
	const canOpenDocument = useSelector(documentActor, (state) =>
		state?.can({ type: "document.opened" })
	);
	const documentIsLoaded = useSelector(
		documentActor,
		(state) => state?.matches("viewing") || state?.matches("editing")
	);
	const documentMetadata = useSelector(documentActor, (state) => state?.context.metadata);

	const currentPage = useMemo(
		() =>
			documentMetadata?.pagesMetadata.find((page) => page.slug === pageSlug) ||
			documentMetadata?.pagesMetadata.find((page) => page.slug === "index") ||
			documentMetadata?.pagesMetadata[0],
		[documentMetadata?.pagesMetadata, pageSlug]
	);

	const pageActor = usePageActor(documentId, currentPage?.id ?? "");
	const pageIsLoaded = useSelector(pageActor, (state) => state?.matches({ data: "editing" }));
	const canOpenPage = useSelector(pageActor, (state) => state?.can({ type: "page.opened" }));

	useEffect(() => {
		if (canOpenDocument) {
			documentActor?.send({ type: "document.opened" });
		}
	}, [documentActor, canOpenDocument]);

	useEffect(() => {
		if (canOpenPage) {
			pageActor?.send({ type: "page.opened" });
		}
	}, [pageActor, canOpenPage]);

	return (
		<>
			<DocumentTopBar />

			{match({ documentMetadata, documentIsLoaded, pageIsLoaded })
				.with({ documentIsLoaded: false }, { pageIsLoaded: false }, () => <CanvasLoading />)
				.with({ documentMetadata: P.nullish }, () => (
					<CanvasFailure
						error="Document not found"
						resetErrorBoundary={() => window.location.reload()}
					/>
				))
				.with({ documentIsLoaded: true, pageIsLoaded: true }, ({ documentMetadata }) => {
					const currentPage =
						documentMetadata?.pagesMetadata.find((page) => page.slug === pageSlug) ||
						documentMetadata?.pagesMetadata.find((page) => page.slug === "index") ||
						documentMetadata?.pagesMetadata[0];

					if (!currentPage) {
						logger.error("unable to find current page", {
							canvasID: documentMetadata?.id,
							pageSlug,
						});
						throw new Error("FIXME");
					}

					return (
						<CanvasPage
							workspaceName={workspaceName}
							documentMetadata={documentMetadata!}
							activePageId={currentPage?.id}
						/>
					);
				})
				.otherwise(() => (
					<CanvasFailure
						error="Something went wrong"
						resetErrorBoundary={() => window.location.reload()}
					/>
				))}
		</>
	);
};

export const Page = dynamic(() => Promise.resolve(PageComponent), { ssr: false });
